import React from 'react';

import Store from 'lib/Store';
import componentName from 'util/componentName';

interface ConnectedWrappedComponentState<P> {
  additionalProps: P;
}

/**
 * Connect the component to the store in the same style as react-redux's connect function.
 */
export default function connectComponentToStore<TStore extends Store, StoreProps, P extends {}>(
  store: TStore,
  mapStoreToProps: (store: TStore, ownProps: P) => StoreProps,
  WrappedComponent: React.ComponentType<P & StoreProps>
): React.ComponentClass<P> {
  class ConnectedWrappedComponent extends React.Component<P, ConnectedWrappedComponentState<StoreProps>> {
    static displayName = `Connect${store.constructor.name}(${componentName(WrappedComponent)})`;

    private _isMounted = false;

    constructor(props: P) {
      super(props);
      this.state = {
        additionalProps: mapStoreToProps(store, props),
      };
      this._isMounted = true;
    }

    override componentDidMount(): void {
      store.addChangeListener(this._handleStoreChange);
    }

    static getDerivedStateFromProps(
      nextProps: P,
      prevState: ConnectedWrappedComponentState<StoreProps>
    ): ConnectedWrappedComponentState<StoreProps> {
      return {additionalProps: mapStoreToProps(store, nextProps)};
    }

    override componentWillUnmount(): void {
      store.removeChangeListener(this._handleStoreChange);
      this._isMounted = false;
    }

    _handleStoreChange = (): void => {
      // Even though we unregister on unmount, an emitChange might update an outer component in a
      // way that unmounts this component, then call _handleStoreChange. So we still need to do an
      // isMounted check to be safe.
      if (!this._isMounted) {
        return;
      }
      this.setState({additionalProps: mapStoreToProps(store, this.props)});
    };

    override render(): JSX.Element {
      return <WrappedComponent {...this.props} {...this.state.additionalProps} />;
    }
  }
  return ConnectedWrappedComponent;
}
